import { createMemo, For, Show } from "solid-js";
import {
  croisementBioagresseur,
  croisementFiliere,
  EFFECTIF_MINIMAL,
  ordreLeviersParCouverture,
} from "../domaine/analyses";
import { tableauDepuisMatrice } from "../export";
import { pluriel } from "../format";
import { couleurClasse, type Analyse } from "./analyse";
import { Barres } from "./Barres";
import { Figure } from "./Figure";
import { Matrice } from "./Matrice";

/**
 * Bioagresseur × levier en petits multiples à échelle partagée (peu de classes,
 * bien peuplées) ; filière × levier en effectifs bruts (beaucoup de filières, de
 * tailles très inégales).
 */
export function VueCroisements(props: { analyse: Analyse }) {
  const p = () => props.analyse.portefeuille;
  const ordreL = createMemo(() => ordreLeviersParCouverture(p().projets, p().leviers));
  const libellesLeviers = () => ordreL().map((j) => p().leviers[j]?.libelle ?? "");

  const classes = createMemo(() =>
    croisementBioagresseur(p().projets, p().leviers, p().classesBioagresseur, ordreL()),
  );
  // Échelle partagée : c'est ce qui rend les panneaux comparables.
  const maxi = () => Math.max(1, ...classes().flatMap((g) => g.parLevier));

  const filieres = createMemo(() =>
    croisementFiliere(p().projets, p().leviers, p().filieres, ordreL()),
  );
  let svgFilieres: SVGSVGElement | undefined;

  return (
    <Show
      when={p().dimensions.leviers}
      fallback={
        <div class="entete-vue">
          <h2>Croisements</h2>
          <p class="explication discret">
            Pas de table des familles de leviers dans la donnée chargée.
          </p>
        </div>
      }
    >
      <Show when={classes().length}>
        <Figure
          id="bioagresseur-levier"
          titre="Bioagresseur × famille de leviers"
          lecture="Un panneau par classe, même échelle. Un projet multi-cible compte dans chaque classe."
          tableau={() => ({
            colonnes: ["bioagresseur", "projets", ...libellesLeviers()],
            lignes: classes().map((g) => [g.libelle, g.projets.length, ...g.parLevier]),
          })}
        >
          <div class="petits-multiples">
            <For each={classes()}>
              {(g) => {
                const couleur = () => couleurClasse(p(), g.classe ?? "");
                return (
                  <section
                    class="multiple"
                    classList={{ attenuee: g.projets.length < EFFECTIF_MINIMAL }}
                    aria-label={g.libelle}
                  >
                    <div class="multiple-tete">
                      <span class="pastille" style={{ background: couleur() }} />
                      <span class="multiple-titre">{g.libelle}</span>
                      <span class="valeur-secondaire">{pluriel(g.projets.length, "projet")}</span>
                    </div>
                    <Show when={g.projets.length < EFFECTIF_MINIMAL}>
                      <p class="explication discret">Effectif trop faible pour conclure.</p>
                    </Show>
                    <Barres
                      lignes={g.parLevier.map((n, k) => ({
                        libelle: libellesLeviers()[k] ?? "",
                        titre: p().leviers[ordreL()[k] ?? -1]?.libelleLong,
                        valeur: n,
                        texte: String(n),
                      }))}
                      max={maxi()}
                      couleur={couleur()}
                    />
                  </section>
                );
              }}
            </For>
          </div>
        </Figure>
      </Show>

      <Show when={filieres().length}>
        <Figure
          id="filiere-levier"
          titre="Filière × famille de leviers"
          lecture={`Effectifs bruts. Moins de ${EFFECTIF_MINIMAL} projets : en retrait.`}
          svg={() => svgFilieres}
          tableau={() =>
            tableauDepuisMatrice(
              filieres().map((g) => `${g.libelle} (${g.projets.length})`),
              libellesLeviers(),
              // eslint-disable-next-line solid/reactivity -- appelé au clic d'export, pas au rendu
              (i, j) => filieres()[i]?.parLevier[j] ?? 0,
            )
          }
        >
          <Matrice
            titre="Matrice filière × famille de leviers"
            type="effectif"
            hauteurCible={60 + filieres().length * 26}
            lignes={filieres().map((g) => ({
              libelle: `${g.libelle} (${g.projets.length})`,
              titre: g.libelle,
            }))}
            colonnes={ordreL().map((j) => ({
              libelle: p().leviers[j]?.libelle ?? "",
              titre: p().leviers[j]?.libelleLong,
            }))}
            valeur={(i, j) => filieres()[i]?.parLevier[j] ?? 0}
            attenuer={(i) => (filieres()[i]?.projets.length ?? 0) < EFFECTIF_MINIMAL}
            infobulle={(i, j, v) => {
              const g = filieres()[i];
              return `${g?.libelle} · ${libellesLeviers()[j]} : ${pluriel(v ?? 0, "projet")} sur ${g?.projets.length}`;
            }}
            refSvg={(e) => (svgFilieres = e)}
          />
        </Figure>
      </Show>
    </Show>
  );
}
